import { isCloudflareOriginErrorStatus, MEDIA_VAULT_UNREACHABLE_TYPE } from '@repo/config/errors';
import type { IAPI, IAPIMessage } from '../../apiResponse';
import { createApiResponseSchema } from '../../apiResponse';
import { getClientConfig } from '../configuration/client-instance';
import { clearProviderHeadersCache } from '../requestBuilder/resolve-provider-headers';
import { APIResponseError } from './api-response-error';
import type { TypeCloudflareReason } from './cloudflare-error';
import { CloudflareError } from './cloudflare-error';
import { detectCloudflareReason } from './detect-cloudflare-reason';
import { isHtmlResponse } from './is-html-response';
import type { IMakeRawRequestParams } from './make-raw-request';
import { makeRawRequest } from './make-raw-request';
import { makeRawRequestWithRetries } from './make-raw-request-with-retries';
import { UnauthorizedError } from './unauthorized-error';

function extractRfc9457Field(data: unknown, field: string): string | undefined {
  if (typeof data === 'object' && data !== null && field in data) {
    const value = (data as Record<string, unknown>)[field];
    if (typeof value === 'string') {
      return value;
    }
  }
  return undefined;
}

function parseErrorResponse(responseData: unknown): {
  errorMessages?: IAPIMessage[];
  errorData: unknown;
  errorDetail?: string;
} {
  try {
    const errorAPISchema = createApiResponseSchema();
    const errorResult = errorAPISchema.safeParse(responseData);
    if (errorResult.success) {
      const apiResponse = errorResult.data as IAPI;
      return { errorMessages: apiResponse.messages, errorData: apiResponse.data };
    }
  } catch {}
  return { errorData: responseData, errorDetail: extractRfc9457Field(responseData, 'detail') };
}

function throwCloudflareError(
  params: IMakeRawRequestParams,
  status: number,
  reason: TypeCloudflareReason,
  code?: string,
): never {
  const error = new CloudflareError({ status, path: params.path, reason, code });
  getClientConfig().onError?.(error);
  throw error;
}

function isMediaVaultUnreachable(status: number, body: unknown): boolean {
  return (
    isCloudflareOriginErrorStatus(status) ||
    extractRfc9457Field(body, 'type') === MEDIA_VAULT_UNREACHABLE_TYPE
  );
}

async function throwResponseError(
  response: Response,
  params: IMakeRawRequestParams,
): Promise<never> {
  if (isHtmlResponse(response)) {
    const reason = await detectCloudflareReason(response);
    throwCloudflareError(params, response.status, reason);
  }

  let parsedBody: unknown;
  try {
    parsedBody = await response.clone().json();
  } catch {}

  if (isMediaVaultUnreachable(response.status, parsedBody)) {
    throwCloudflareError(
      params,
      response.status,
      'tunnel',
      extractRfc9457Field(parsedBody, 'cloudflareCode'),
    );
  }

  const { errorMessages, errorData, errorDetail } = parseErrorResponse(parsedBody);
  const error = new APIResponseError({
    message: errorDetail ?? `API request failed with status ${response.status}`,
    status: response.status,
    statusText: response.statusText,
    messages: errorMessages,
    data: errorData,
    path: params.path,
  });
  getClientConfig().onError?.(error);
  throw error;
}

export async function makeCheckedRequest(params: IMakeRawRequestParams): Promise<Response> {
  let response = await makeRawRequestWithRetries(params);

  if (response.status === 401) {
    clearProviderHeadersCache();
    const config = getClientConfig();
    const freshHeaders = await config.onUnauthorized?.();
    if (freshHeaders) {
      const merged = {
        ...params.options,
        headers: { ...params.options?.headers, ...freshHeaders },
      };
      response = await makeRawRequest({ ...params, options: merged });
    } else {
      throw new UnauthorizedError();
    }
  }

  if (!response.ok) {
    return throwResponseError(response, params);
  }

  return response;
}
